import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, Save, ShieldCheck } from "lucide-react";

export default function SettingsPage() {
  const { hasPermission } = useAuth();
  const [settings, setSettings] = useState([]);
  const [edits, setEdits] = useState({});
  const canEdit = hasPermission("SETTINGS.EDIT");

  const load = useCallback(async () => {
    try { setSettings((await api.get("/v1/settings")).data.data); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    try {
      await api.put(`/v1/settings/${key}`, { value: edits[key] });
      toast.success("Setting saved");
      setEdits((p) => { const n = { ...p }; delete n[key]; return n; });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const byCat = settings.reduce((acc, s) => { (acc[s.category] = acc[s.category] || []).push(s); return acc; }, {});
  const order = ["Integration", "General", "Security", "Custom"];

  return (
    <div data-testid="settings-page">
      <PageHeader title="System Settings" subtitle="Platform configuration & integration API keys" />

      {order.filter((c) => byCat[c]).map((cat) => (
        <div key={cat} className="mb-7">
          <div className="flex items-center gap-2 mb-3">
            {cat === "Integration" ? <KeyRound className="h-4 w-4 text-primary" /> : <ShieldCheck className="h-4 w-4 text-slate-400" />}
            <h3 className="font-semibold text-slate-800">{cat}</h3>
            {cat === "Integration" && <span className="text-xs text-slate-400">— add your OpenAI Business API key here</span>}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
            {byCat[cat].map((s) => (
              <div key={s.key} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`setting-${s.key}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-mono text-xs font-semibold text-slate-700">{s.key}</div>
                  {s.isSecret && s.hasValue && <div className="text-xs text-emerald-600 mt-0.5">Configured ({s.value})</div>}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type={s.isSecret ? "password" : "text"}
                    defaultValue={s.isSecret ? "" : (typeof s.value === "boolean" ? String(s.value) : s.value)}
                    placeholder={s.isSecret ? "Enter to update…" : ""}
                    onChange={(e) => setEdits((p) => ({ ...p, [s.key]: s.key.includes("enabled") ? e.target.value === "true" : e.target.value }))}
                    disabled={!canEdit}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm w-64 outline-none focus:ring-2 focus:ring-primary/40 disabled:bg-slate-100"
                    data-testid={`setting-input-${s.key}`}
                  />
                  {canEdit && edits[s.key] !== undefined && (
                    <button onClick={() => save(s.key)} className="p-2 rounded-lg bg-primary text-white" data-testid={`setting-save-${s.key}`}>
                      <Save className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
